import * as React from 'react';
import CheckBox from '../../../../components/Checkbox';
import Icons from '../../../../icons';
import { filterConstToShowInCombinedView } from '../../constants';
import UserImage from '../../../../components/UserImage/index';

const getDefaultItemsToShow = (isUsersFilter) =>
    !isUsersFilter ? filterConstToShowInCombinedView.itemToShow : filterConstToShowInCombinedView.userItemToShow;

const getInitialItemsToShow = ({ showHeader, isUsersFilter, filterOptions }) => {
    if (!showHeader) {
        return getDefaultItemsToShow(isUsersFilter);
    }
    const keys = Object.keys(filterOptions);
    return keys.length > 0 && filterOptions[keys[0]].options.length;
};

const MultiFilter = (props) => {
    const {
        filterOptions: filterOptionsProp,
        showHeader,
        isUsersFilter,
        openRight,
        showParentOption,
        isParentCheckIcon,
        isOnlyOption,
        onCheckChange,
        onClose,
    } = props;

    const filterRef = React.useRef(null);
    const onCloseRef = React.useRef(onClose);
    onCloseRef.current = onClose;

    const [filterOptions, setFilterOptions] = React.useState(filterOptionsProp);
    const [filterKeys, setFilterKeys] = React.useState(Object.keys(filterOptionsProp));
    const [itemsToShow, setItemsToShow] = React.useState(() => getInitialItemsToShow(props));
    const [expanded, setExpanded] = React.useState(false);

    const defaultItemsToShow = getDefaultItemsToShow(isUsersFilter);

    // run once on mount: register outside-click listener and mark parents whose
    // children are all checked (mirrors the previous componentDidMount behaviour)
    React.useEffect(() => {
        const handleOutsideClick = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                onCloseRef.current();
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);

        if (filterOptionsProp) {
            const keys = Object.keys(filterOptionsProp);
            const updatedFilterOptions = {};
            keys.forEach((parentKey) => {
                const options = filterOptionsProp[parentKey].options.map((option) => ({ ...option }));
                const globalCheck = options.filter((option) => option.checked === false);
                const checked = globalCheck.length === 0 || filterOptionsProp[parentKey].checked ? true : filterOptionsProp[parentKey].checked;
                updatedFilterOptions[parentKey] = { ...filterOptionsProp[parentKey], options, checked };
            });

            setFilterOptions(updatedFilterOptions);
            setFilterKeys(keys);
        }

        return () => document.removeEventListener('mousedown', handleOutsideClick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // console.log(filterOptions, filterOptionsProp, '-----------filterOptions');
    

    const onCloseFilter = () => {
        onClose();
    };

    const cloneFilterOptions = (source) => {
        const clone = {};
        Object.keys(source).forEach((key) => {
            clone[key] = { ...source[key], options: source[key].options.map((option) => ({ ...option })) };
        });
        return clone;
    };

    // check/uncheck parent and, based on the parent check/uncheck, cascade the value to every child
    const onChangeParent = (parentKey) => {
        const updatedFilterOptions = cloneFilterOptions(filterOptions);
        updatedFilterOptions[parentKey].checked = !updatedFilterOptions[parentKey].checked;
        updatedFilterOptions[parentKey].options.forEach((option) => {
            option.checked = updatedFilterOptions[parentKey].checked;
        });

        setFilterOptions(updatedFilterOptions);
        if (onCheckChange) {
            onCheckChange(updatedFilterOptions);
        }
    };

    // handle a child checkbox change and, based on the children, recalculate the parent
    // check/uncheck state - e.g. if all children are checked, check the parent too
    const onChangeChild = (parentKey, childKey) => {
        const updatedFilterOptions = cloneFilterOptions(filterOptions);
        updatedFilterOptions[parentKey].options[childKey].checked = !updatedFilterOptions[parentKey].options[childKey].checked;

        if (!updatedFilterOptions[parentKey].options[childKey].checked) {
            updatedFilterOptions[parentKey].checked = false;
        }

        const globalCheck = updatedFilterOptions[parentKey].options.filter((option) => option.checked === false);
        if (globalCheck.length === 0) {
            updatedFilterOptions[parentKey].checked = true;
        }

        setFilterOptions(updatedFilterOptions);
        if (onCheckChange) {
            onCheckChange(updatedFilterOptions);
        }
    };

    const onOnlyClick = (parentKey, childKey, childValue) => {
        const updatedFilterOptions = cloneFilterOptions(filterOptions);
        updatedFilterOptions[parentKey].options[childKey].checked = true;
        updatedFilterOptions[parentKey].checked = false;

        const globalCheck = updatedFilterOptions[parentKey].options.filter((option) => option.value !== childValue);

        if (globalCheck.length !== 0) {
            updatedFilterOptions[parentKey].options.forEach((option) => {
                globalCheck.forEach((otherOption) => {
                    if (option.value === otherOption.value) {
                        option.checked = false;
                    }
                });
            });
        } else {
            updatedFilterOptions[parentKey].checked = true;
        }

        setFilterOptions(updatedFilterOptions);
        if (onCheckChange) {
            onCheckChange(updatedFilterOptions);
        }
    };

    const showMore = (filterKey) => {
        if (itemsToShow === defaultItemsToShow) {
            setItemsToShow(filterOptions[filterKey].options.length);
            setExpanded(true);
        } else {
            setItemsToShow(defaultItemsToShow);
            setExpanded(false);
        }
    };

    return (
        <div className={openRight ? 'dropDownMenu overlap onClick openRight' : 'dropDownMenu overlap onClick filter'} ref={filterRef}>
            {showHeader && <div className="header"><header className="alignMiddle">Filters</header></div>}
            {filterKeys?.map((filterKey, key) => {
                let showMoreLessLink = null;
                if (!isUsersFilter && filterOptions[filterKey].options.length > defaultItemsToShow) {
                    showMoreLessLink = expanded ? (
                        <a className="linkSmall" href="javascript:void(0)" onClick={() => showMore(filterKey)}>show less</a>
                    ) : (
                        <a className="linkSmall" href="javascript:void(0)" onClick={() => showMore(filterKey)}>show more</a>
                    );
                }

                return (
                <div key={filterKey}>
                    {/* render the parent of filter option */}
                    {
                        !openRight &&
                        !showHeader &&
                        <div className="alignMiddle rowFlex rowSpaceBetween mb__8">
                            <h3>{filterOptions?.[filterKey].name}</h3>
                            {showMoreLessLink}
                            {
                                isUsersFilter && !showHeader && filterOptions[filterKey].options.length > defaultItemsToShow && expanded &&
                                <a className="linkSmall" href="javascript:void(0)" onClick={() => showMore(filterKey)}>show less</a>
                            }
                        </div>
                    }
                    {
                        showParentOption &&
                        <a href="javascript:void(0)" className="parentCheckBox" key={filterKey}>
                            { isParentCheckIcon ?
                                <h2 className="mb__12 rowFlex">{filterOptions?.[filterKey].name}<span className="ml" onClick={() => onChangeParent(filterKey)} id={`${filterKey}_${key}`}><Icons name="multiCheck" className={`icon multiCheck ${ filterOptions[filterKey].checked === true ? 'active' : ''}`} /></span></h2>
                                :
                                <>
                                    <CheckBox handleCheckbox={() => onChangeParent(filterKey)} id={`${filterKey}_${key}`} label={filterOptions?.[filterKey].name} checked={ filterOptions[filterKey].checked === true } />
                                    <label htmlFor={`${filterKey}_${key}`}></label>
                                </>
                            }
                        </a>
                    }
                    {/* render the  child of the parent  */}
                    <ul className={isUsersFilter ? (expanded ? 'colFlex listView' : ' rowFlex flexWrap ') : 'colFlex'}>
                        {filterOptions?.[filterKey]?.options?.slice(0, itemsToShow).map((optionData, optionKey) => {
                            const onlyLink = isOnlyOption && filterOptions[filterKey].options.length > 1 ? (
                                <a className="onlyFilter" onClick={() => onOnlyClick(filterKey, optionKey, optionData.value)} href="javascript:void(0)">only</a>
                            ) : '';

                            if (isUsersFilter) {
                                return (
                                    <li className="userGroup flexNone" title={expanded ? '' : optionData.name} key={optionData.value}>
                                        <div className="userPic userPicIcon" onClick={() => onChangeChild(filterKey, optionKey)}>
                                           <UserImage profileUrl={optionData.userImage} className="profilePic" profileName={optionData.name}/><label htmlFor={`${optionData.name}_${optionKey}`}>{optionData.name}</label>
                                            {(optionData.checked === true || filterOptions[filterKey].checked) ? <span className='badges profileChecked checkedTop green'></span> : null}</div>
                                        {onlyLink}
                                    </li>
                                );
                            }

                            return (
                                <li key={optionData.value}>
                                     <a className="flexGrow" href="javascript:void(0)">
                                           <CheckBox handleCheckbox={() => onChangeChild(filterKey, optionKey)}
                                                item={{id:`${optionData.name}_${optionKey}`,label:optionData.name, checked:optionData.checked === true || filterOptions[filterKey].checked }} key={`${optionData.name}_${optionKey}`}/>
                                    </a>
                                    {onlyLink}
                                </li>
                            );
                        })}
                        { (isUsersFilter && !expanded && filterOptions[filterKey].options.length > filterConstToShowInCombinedView.userItemToShow) ?
                            <li className="userGroup flexNone">
                                <div className="userPic userPicIcon" onClick={() => showMore(filterKey)}>
                                    <span className="moreUserSpan userPic userPicIcon">+{filterOptions[filterKey].options.length - itemsToShow}</span>
                                    <a className="linkSmall" href="javascript:void(0)">more...</a>
                                </div>
                            </li>
                            :
                            null
                        }
                    </ul>
                </div>
                );
            })}
            {/* render the  footer for  apply or  clear  */}
            {showHeader &&
                <footer>
                    <div className="actions alignRight">
                        <a className="btn link" href="javascript:void(0)" onClick={onCloseFilter}>Close</a>
                        {/* <a className="btn link linkGreen" href="javascript:void(0)">Apply</a> */}
                        {/* <a className="btn link linkGreen" href="javascript:void(0)" onClick={onApplyFilter}>Apply</a> */}
                    </div>
                </footer>
            }
        </div>
    );
}

export default MultiFilter;
